export const namespaced = true;

// Libraries are served by this same site, so embed URLs follow the domain the page is viewed on
const librariesUrl = `${window.location.origin}/libraries`;

export const state = {
  // NOTE: Widget id must be a string
  widgets: [
    {
      id: "1",
      name: "Location",
      nameFr: "Emplacement",
      tag: "open-services-sgc-lookup",
      return: "sgc",
      widget_tag: `open-services-sgc-lookup`,
      widget_source_en: `<a href="https://www23.statcan.gc.ca/imdb/p3VD.pl?Function=getVD&TVD=314300" target="_blank">Standard Geographical Classification (SGC) 2016, Statistics Canada</a>`,
      widget_source_fr: `<a href="https://www23.statcan.gc.ca/imdb/p3VD_f.pl?Function=getVD&TVD=314300" target="_blank">Classification géographique type (CGT) 2016, Statistique Canada</a>`,
      collapses: [
        {
          title: `Why can't I find information for my industry and/or location?`,
          title_fr: `Pourquoi je ne peux trouver l'information pour mon industrie et/ou l'emplacement?`,
          text: `<p>
            The information may not exist in the combination you've chosen. For example, poppy flowers are not endemic to Canada so poppy farming is not likely to yield 
            results in any province selected.
            </p>
            <p>
            Sometimes an industry is represented by such a small number of businesses in a certain location that we can't share the data without violating the privacy of 
            those businesses. Other times, the information may be inaccurate or missing. In these cases, Statistics Canada has flagged the data as unreliable and we have 
            removed it from our collection.
            </p>`,
          text_fr: `<p>
            L'information peut ne pas existé dans la combinaison que vous avez choisi. Par exemple, le coquelicot n'est pas une espèce endémique du Canada donc l'agriculture 
            du coquelicot ne produirait probablement pas des résultats dans l'une ou l'autre des provinces sélectionnées.
            </p>
            <p>
            Parfois une industrie est représenté par un faible nombre d'entreprises dans un certain emplacement que nous ne pouvons pas partager les données sans 
            faire abstraction à la protection de la vie privée de ses entrprises. En d'autre temps, l'information peut être inexacte ou manquant. Dans ces cas, Statistique 
            Canada a signalé les données comme un fonctionnement incertain et nous les avons retiré de notre collection.
            </p>`
        },
        {
          title: `Why am I being shown a different location than the one I chose?`,
          title_fr: `Pourquoi je suis montré un emplacement différent de ce que j'ai choisi?`,
          text: `<p>
            We always show the most detailed and specific information available. However, if information is not available for your choices, the program will show the most 
            similar industry or location for which information is available.
            </p>
            <p>
            In the case of location, this often means showing you national instead of provincial information.
            </p>`,
          text_fr: `<p>
            Nous montrons toujours l'information la plus détaillé et spécifique disponible. Cependant, si l'information n'est pas disponible pour vos choix, le programme va 
            montrer l'industrie ou l'emplacement le plus similaire dont l'information est disponible.
            </p>
            <p>
            Dans le cas de l'emplacement, ceci veut souvent dire qu'on vous montre l'information national au lieu de provincial.
            </p>`
        }
      ],
      widget_url: `${librariesUrl}/open-services-sgc-lookup.umd.min.js`,
      widget_urlFr: `${librariesUrl}/open-services-sgc-lookup.umd.min.js`,
      description: `Allows a user to input a municipality, or select a province/territory from a dropdown menu and returns the
      associated code from the Standard Geographical Classification (SGC) system.`,
      descriptionFr: `Permet à un utilisateur d'entrer une municipalité ou de sélectionner une province ou un territoire du menu 
      déroulant et d'obtenir en retour le code associé du système de la Classification géographique type (CGT).`,
      imgSrc: require("../../../public/images/location_example_en.jpg"),
      imgSrcFr: require("../../../public/images/location_example_fr.jpg"),
      exampleText: `<p>As a user types "Toronto", the autocomplete shows the
      preferred terms that match the client's input.
      </p>
      <br />
      <p>
      As the user selects "Toronto, Ontario", it returns the selection in an object format with the associated <abbr title="Standard Geographical Classification">SGC</abbr> code.
     </p>
    `,
      exampleTextFr: `<p>Au moment que l'utilisateur tape «&nbsp;Toronto&nbsp;», l'autocomplétion montre 
      les termes préférentiels qui apparient avec l'entrée de l'utilisateur.</p>
      <br />
      <p>Au moment que l'utilisateur sélectionne «&nbsp;Toronto, Ontario&nbsp;», cela retourne la sélection en format objet avec le code <abbr title="Classification géographique type">CGT</abbr> associé.
      </p>
      `,
      customAttributes: [
        {
          id: 1,
          name: "province",
          default: "false",
          description:
            "Add/set the 'province' element within the widget tag to 'false' if you want a city selection, or to 'true' for a province selection. Default value is set to 'false'."
        },
        {
          id: 2,
          name: "multiple",
          default: "false",
          description:
            "Add/set the 'multiple' element within the widget tag to 'false' if you want a single selection, or to 'true' for multiple selections. Default value is set to 'false'."
        },
        {
          id: 3,
          name: "lang",
          default: "en",
          description:
            "Add/set the 'lang' element within the widget tag to the language of your choice ('en' is English and 'fr' is French). Default value is set to 'en'."
        },
        {
          id: 4,
          name: "show-label",
          default: "true",
          description:
            "Add/set the 'show-label' element within the widget tag to 'false' if you want no label to show, or 'true' to show a label. Default value is set to 'true'."
        },
        {
          id: 5,
          name: "label",
          default: "Select a City / Select Province",
          description:
            "Add/set the 'label' element within the widget tag with text containing the customized label to show. If the 'label' element is empty or missing, a default label will be shown."
        },
        {
          id: 6,
          name: "placeholder-text",
          default: "Type to search",
          description:
            "Add/set the 'placeholder-text' element within the widget tag with text containing the customized placeholder to show. If the 'placeholder-text' element is not included, a default placeholder will be shown. To hide the default text, set the 'placeholder-text' element to an empty string (&quot;&nbsp;&quot;)."
        },

        {
          id: 7,
          name: "show-code",
          default: "true",
          description: `Add/set the 'show-code' element within the widget tag to 'false' if you do not want the <abbr title="Standard Geographical Classification">SGC</abbr> code number to be displayed, or set to 'true' to show the <abbr title="Standard Geographical Classification">SGC</abbr> code number. Default value is set to 'true'.`
        },
        {
          id: 8,
          name: "visibility",
          default: "true",
          description: `Add/set the 'visibility' element within the widget tag to 'false' if you want to hide the input box, or 'true' to show it. If set to 'false', the 'input-code' tag element will have to be assigned a default <abbr title="Standard Geographical Classification">SGC</abbr> (location) code in order for the widget to function properly. Default value is set to 'true'.`
        },
        {
          id: 9,
          name: "input-code",
          default: "",
          description: `Add/set the 'input-code' element within the widget tag with text containing the <abbr title="Standard Geographical Classification">SGC</abbr> code to use by default. If 'input-code' element is empty or missing, no default title will be shown and normal search shows.`
        }

        /* {
          id: 10,
          name: "@change",
          default: "",
          description:
            "If using Vue.js, the element '@change' contains the name for the function to use to retrieve the data in an object format."
        } */
      ],
      customAttributesFr: [
        {
          id: 1,
          name: "province",
          default: "false",
          description:
            "Ajouter/mettre l'élément «&nbsp;province&nbsp;» de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous voulez une sélection de ville ou à «&nbsp;true&nbsp;» (vrai) pour une sélection de province. La valeur par défaut est mise à «&nbsp;false&nbsp;»."
        },
        {
          id: 2,
          name: "multiple",
          default: "false",
          description:
            "Ajouter/mettre l'élément «&nbsp;multiple&nbsp;» de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous voulez une seule sélection ou à «&nbsp;true&nbsp;» (vrai) pour une sélection multiple. La valeur par défaut est mise à «&nbsp;false&nbsp;»."
        },
        {
          id: 3,
          name: "lang",
          default: "en",
          description:
            "Ajouter/mettre l'élément «&nbsp;lang&nbsp;» de la balise du widget à la langue de votre choix (fr = français et en = anglais). La valeur par défaut est mise à «&nbsp;en&nbsp;»."
        },
        {
          id: 4,
          name: "show-label",
          default: "true",
          description:
            "Ajouter/mettre l'élément «&nbsp;show-label&nbsp;» (affiché l'étiquette) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous ne voulez pas afficher d'étiquette ou à «&nbsp;true&nbsp;» (vrai) pour afficher une étiquette. La valeur par défaut est mise à «&nbsp;true&nbsp;»."
        },
        {
          id: 5,
          name: "label",
          default: "Sélectionnez une ville / Sélectionnez une province",
          description:
            "Ajouter/mettre l'élément «&nbsp;label&nbsp;» (étiquette) de la balise du widget avec du texte contenant l'étiquette personnalisé à montrer. Si l'élément «&nbsp;label&nbsp;» est vide ou manquant, une étiquette par défaut sera affichée."
        },
        {
          id: 6,
          name: "placeholder-text",
          default: "Tapez pour rechercher",
          description:
            "Ajouter/mettre l'élément «&nbsp;placeholder-text&nbsp;» (espace de texte réservé) de la balise du widget avec du texte contenant l'espace personnalisé réservé à montrer. Si l'élément «&nbsp;placeholder-text&nbsp;» n'est pas inclus, un espace réservé par défaut sera affiché. Pour cacher le texte par défaut, mettre l'élément «&nbsp;placeholder-text&nbsp;» à une chaîne vide (&quot;&nbsp;&quot;)."
        },
        {
          id: 7,
          name: "show-code",
          default: "true",
          description: `Ajouter/mettre l'élément «&nbsp;show-code&nbsp;» (affiché le code) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous ne voulez pas afficher le numéro de code <abbr title="Classification géographique type">CGT</abbr> ou à «&nbsp;true&nbsp;» (vrai) pour afficher le numéro de code <abbr title="Classification géographique type">CGT</abbr>. La valeur par défaut est mise à «&nbsp;true&nbsp;».`
        },
        {
          id: 8,
          name: "visibility",
          default: "true",
          description: `Ajouter/mettre l'élément «&nbsp;visibility&nbsp;» (visibilité) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous voulez cacher la boîte d'entrée ou à «&nbsp;true&nbsp;» (vrai) pour la montrer. Si l'élément est mis sur «&nbsp;false&nbsp;», la balise «&nbsp;input-code&nbsp;» devra se voir attribuer un code <abbr title="Classification géographique type">CGT</abbr> (emplacement) par défaut afin que le widget fonctionne correctement. La valeur par défaut est mise à «&nbsp;true&nbsp;».`
        },
        {
          id: 9,
          name: "input-code",
          default: "",
          description: `Ajouter/mettre l'élément «&nbsp;input-code&nbsp;» (saisie de code) de la balise du widget avec du texte contenant le code <abbr title="Classification géographique type">CGT</abbr> à utiliser par défaut. Si l'élément «&nbsp;input-code&nbsp;» est vide ou manquant, aucun titre par défaut va être affiché et la recherche normale est affichée.`
        }
        /* {
          id: 10,
          name: "@change",
          default: "",
          description:
            "Si Vue.js est utilisé, l'élément «&nbsp;@change&nbsp;» contient le nom de la fonction à utiliser pour récupérer les données en format objet."
        } */
      ]
    },
    {
      id: "2",
      name: "Industry",
      nameFr: "Industrie",
      return: "naics",
      tag: "open-services-naics-lookup",
      widget_tag: `open-services-naics-lookup`,
      widget_source_en: `<a href="https://www23.statcan.gc.ca/imdb/p3VD.pl?Function=getVD&TVD=1181553" target="_blank">North American Industry Classification System (NAICS) Canada 2017, Statistics Canada</a>`,
      widget_source_fr: `<a href="https://www23.statcan.gc.ca/imdb/p3VD_f.pl?Function=getVD&TVD=1181553" target="_blank">Système de classification des industries de l'Amérique du Nord (SCIAN) Canada 2017, Statistique Canada</a>`,
      collapses: [
        {
          title: `About the Industry Widget Methodology (BizPaL Hybrid&nbsp;<abbr title="North American Industry Classification System">NAICS</abbr>)`,
          title_fr: `À propos de la méthodologie du widget d'industrie (hybride du&nbsp;<abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>&nbsp;PerLE)`,
          text: `<p>
            The industry widget helps users to find their industry using a convenient type-ahead user interface that uses title or keywords to identify their industry, based on a 
            hybrid of the North American Industry Classification System (NAICS) 2017, version 3.0.
            </p>`,
          text_fr: `<p>
            Le widget d'industrie aide les utilisateurs à trouver leur industrie à l'aide d'une interface utilisateur de saisie anticipée pratique qui utilise un titre ou des 
            mots-clés pour identifier leur industrie, basée sur un hybride du Système de classification des industries de l'Amérique du Nord (SCIAN) 2017, version 3.0.
            </p>`
        },
        {
          title: `What is&nbsp;<abbr title="North American Industry Classification System">NAICS</abbr>?`,
          title_fr: `Qu'est-ce que le&nbsp;<abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>?`,
          text: `<p>
            The <abbr title="North American Industry Classification System">NAICS</abbr> is an industry classification system developed by the statistical agencies of Canada, Mexico 
            and the United States. Created against the background of the North American Free Trade Agreement, it is designed to provide common definitions of the industrial structure 
            of the three countries and a common statistical framework to facilitate the analysis of the three economies. The hierarchical structure of 
            <abbr title="North American Industry Classification System">NAICS</abbr> designates the sector (2 digit codes), the subsector (3 digit), the industry group (4-digit), 
            the industries (5-digit) or Canadian industries (6 digit).
            </p>`,
          text_fr: `<p>
            Le <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> est un système de classification des industries élaboré par les organismes 
            statistiques du Canada, du Mexique et des États-Unis. Créé dans le contexte de l'Accord de libre-échange nord-américain, il est conçu pour fournir des définitions communes 
            de la structure industrielle des trois pays et un cadre statistique commun pour faciliter l'analyse des trois économies. La structure hiérarchique du 
            <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> désigne les industries par secteur (codes à 2 chiffres), sous-secteur (3 chiffres), 
            groupes d'industries (4 chiffres), classe d'industries (5 chiffres) ou classe canadienne (6 chiffres).
            </p>`
        },
        {
          title: `Are all the&nbsp;<abbr title="North American Industry Classification System">NAICS</abbr>&nbsp;code available?`,
          title_fr: `Est-ce que tous les codes&nbsp;<abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>&nbsp;sont offerts?`,
          text: `<p>
            In an effort to simplify the end-user's ability to self-identify their industry, this lookup employs a hybrid of <abbr title="North American Industry Classification System">NAICS</abbr> 
            that does not include every code at every level. However, all industries in our widget are captured by a <abbr title="North American Industry Classification System">NAICS</abbr> 
            code. Some industries can be selected at most granular level (Canadian industry), while others are grouped under a broader category (industry group or subsectors). 
            The lookup also adds plain-language titles as well as added keywords to assist with self-identifying to an industry. 
            </p>
            <p>
            As the original use-case for this widget, this hybrid version of <abbr title="North American Industry Classification System">NAICS</abbr> provided excellent search results 
            for permits and licenses as well as the programs and services from multiple levels of government in Canada.
            </p>
            <p>
            If your use-case or application requires the deepest level of specificity for industry, we recommend you use our second industry lookup widget, which employs the full scope 
            of <abbr title="North American Industry Classification System">NAICS</abbr> rather than a reduced version.
            </p>`,
          text_fr: `<p>
            Dans un effort pour simplifier la capacité de l'utilisateur final à identifier son industrie, cet outil de recherche utilise un hybride du 
            <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> qui n'inclut pas tous les codes à tous les niveaux. Cependant, tous les industries de notre widget sont capturés 
            sous un code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>. Certaines industries peuvent être sélectionnés à leur niveau 
            <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> le plus granulaire (classe canadienne), tandis que d'autres sont regroupées dans une catégorie plus large 
            (groupe industrie ou sous-secteurs). La recherche ajoute également des titres en langage clair ainsi que des mots-clés supplémentaires pour aider à choisir son industrie.
            </p>
            <p>
            En tant que cas d'utilisation d'origine pour ce widget, cette version hybride du <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> fournissait d'excellents résultats 
            de recherche pour les permis et licences ainsi que les programmes et services offerts par les ordres de gouvernement au Canada.
            </p>
            <p>
            Si votre cas d'utilisation ou votre application nécessite le plus haut niveau de spécificité pour les industries, nous vous recommandons d'utiliser notre deuxième widget, qui utilise toute la portée du 
            <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> plutôt qu'une version réduite.
            </p>`
        },
        {
          title: `Why assign keyword to&nbsp;<abbr title="North American Industry Classification System">NAICS</abbr>&nbsp;code?`,
          title_fr: `Pourquoi des mots-clés sont-ils attribués aux codes&nbsp;<abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>?`,
          text: `<p>
            We assign keywords to <abbr title="North American Industry Classification System">NAICS</abbr> codes in our database to help users identify their industry code using 
            natural terminology. Our systems capture search terms that render zero results, and these are reviewed on a regular basis and assigned as keywords to matching 
            industry codes. This iterative process helps to refine the search, on an ongoing basis.  
            </p>
            <p>
            For example: 
            </p>
            <ul>
            <li>A user operating a hot dog stand will find their industry falls under 722330 - Mobile food concessions.</li>
            <li>A user retailing arts supplies will find their industry falls under 453999 - Specialty retail stores.</li>
            </ul>`,
          text_fr: `<p>
            Nous attribuons des mots-clés aux codes <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> dans notre base de données pour aider les utilisateurs à identifier 
            e code correspondant à leur industrie à l'aide d'une terminologie naturelle. Nos systèmes capturent les termes de recherche qui ne produisent aucun résultat, et ceux-ci sont examinés de façon régulière et 
            sont attribués en tant que mots-clés aux codes d'industrie correspondants. Ce processus itératif nous permet d'affiner la recherche de façon continue.
            </p>
            <p>
            Par exemple:
            </p>
            <ul>
            <li>Un utilisateur exploitant un stand de hot-dogs constatera que son industrie relève du code 722330 - Concessions alimentaires mobiles.</li>
            <li>Un utilisateur qui vend au détail du matériel d'artistes trouvera que son industrie relève du code 453999 - Magasins de détail spécialisé.</li>
            </ul>`
        },
        {
          title: `How soon is the widget updated after a new version of&nbsp;<abbr title="North American Industry Classification System">NAICS</abbr>&nbsp;is released?`,
          title_fr: `Le widget est-il mis à jour après la sortie d'une nouvelle version du&nbsp;<abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>?`,
          text: `<p>
            We aim to update our database within 6 months from the release of the correspondence tables that compare changes from the new version to the previous version.   
            </p>`,
          text_fr: `<p>
            Notre objectif est de mettre à jour la base de données dans les 6 mois suivant la publication des tables de correspondance comparant les évolutions de la nouvelle version à la version précédente.  
            </p>`
        },
        {
          title: `Where can I find the raw dataset?`,
          title_fr: `Où puis-je trouver l'ensemble de données brutes?`,
          text: `<p>
            <a href="https://www.statcan.gc.ca/eng/subjects/standard/naics/2017/v3/index" target="_blank"><abbr title="North American Industry Classification System">NAICS</abbr> Canada 2017 Version 3.0, Statistics Canada</a>
            </p>`,
          text_fr: `<p>
            <a href="https://www.statcan.gc.ca/fra/sujets/norme/scian/2017/v3/index" target="_blank"><abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> Canada 2017 version 3.0, Statistique Canada</a>
            </p>`
        }
      ],
      widget_url: `${librariesUrl}/open-services-naics-lookup.umd.min.js`,
      widget_urlFr: `${librariesUrl}/open-services-naics-lookup.umd.min.js`,
      description: `Allows a user to input an industry and returns the
      associated code from the North American Industry Classification
      System (NAICS).`,
      descriptionFr: `Permet à un utilisateur d'entrée une industrie 
      et d'obtenir en retour le code associé du Système de classification 
      des industries de l'Amérique du Nord (SCIAN).`,
      imgSrc: require("../../../public/images/industry_example_en.jpg"),
      imgSrcFr: require("../../../public/images/industry_example_fr.jpg"),
      exampleText: `<p>As the user types "hot dog", the autocomplete shows the
      preferred terms that match the user's input.
      </p>
     <br />
     <p>
      As the user selects "Mobile food concession", it returns the selection in an object format with the associated <abbr title="North American Industry Classification System">NAICS</abbr> code.
     </p>
    `,
      exampleTextFr: `<p>Au moment qu'un utilisateur tape «&nbsp;hot dog&nbsp;», l'autocomplétion 
      montre les termes préférentiels qui apparient avec l'entrée de l'utilisateur.</p>
      <br />
      <p>Au moment que l'utilisateur sélectionne «&nbsp;Concessions, stands ou camions alimentaires mobiles&nbsp;», cela retourne la sélection en format objet avec le code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> associé.
      </p>
    `,
      customAttributes: [
        {
          id: 1,
          name: "multiple",
          default: "false",
          description:
            "Add/set the 'multiple' element within the widget tag to 'false' if you want a single selection, or to 'true' for multiple selections. Default value is set to 'false'."
        },
        {
          id: 2,
          name: "lang",
          default: "en",
          description:
            "Add/set the 'lang' element within the widget tag to the language of your choice ('en' is English and 'fr' is French). Default value is set to 'en'."
        },
        {
          id: 3,
          name: "show-label",
          default: "true",
          description:
            "Add/set the 'show-label' element within the widget tag to 'false' if you want no label to show, or 'true' to show a label. Default value is set to 'true'."
        },
        {
          id: 4,
          name: "label",
          default: "Select an Industry / Select Industries",
          description:
            "Add/set the 'label' element within the widget tag with text containing the customized label to show. If 'label' element is empty or missing, a default label will be shown."
        },
        {
          id: 5,
          name: "placeholder-text",
          default: "Type to search",
          description:
            "Add/set the 'placeholder-text' element within the widget tag with text containing the customized placeholder to show. If the 'placeholder-text' element is not included, a default placeholder will be shown. To hide the default text, set the 'placeholder-text' element to an empty string (&quot;&nbsp;&quot;)."
        },
        {
          id: 6,
          name: "show-code",
          default: "true",
          description: `Add/set the 'show-code' element within the widget tag to 'false' if you do not want the <abbr title="North American Industry Classification System">NAICS</abbr> code number to be displayed, or set to 'true' to show the <abbr title="North American Industry Classification System">NAICS</abbr> code number. Default value is set to 'true'.`
        },
        {
          id: 7,
          name: "visibility",
          default: "true",
          description: `Add/set the 'visibility' element within the widget tag to 'false' if you want to hide the input box, or 'true' to show it. If set to 'false', the 'input-code' tag element will have to be assigned a default <abbr title="North American Industry Classification System">NAICS</abbr> (industry) code in order for the widget to function properly. Default value is set to 'true'.`
        },
        {
          id: 8,
          name: "input-code",
          default: "",
          description: `Add/set the 'input-code' element within the widget tag with text containing the <abbr title="North American Industry Classification System">NAICS</abbr> code to use by default. If 'input-code' element is empty or missing, no default title will be shown and normal search shows.`
        }
        /* {
          id: 9,
          name: "@change",
          default: "",
          description:
            "If using Vue.js, the element '@change' contains the name for the function to use to retrieve the data in an object format."
        } */
      ],
      customAttributesFr: [
        {
          id: 1,
          name: "multiple",
          default: "false",
          description:
            "Ajouter/mettre l'élément «&nbsp;multiple&nbsp;» de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous voulez une seule sélection ou à «&nbsp;true&nbsp;» (vrai) pour une sélection multiple. La valeur par défaut est mise à «&nbsp;false&nbsp;»."
        },
        {
          id: 2,
          name: "lang",
          default: "en",
          description:
            "Ajouter/mettre l'élément «&nbsp;lang&nbsp;» de la balise du widget à la langue de votre choix (fr = français et en = anglais). La valeur par défaut est mise à «&nbsp;en&nbsp;»."
        },
        {
          id: 3,
          name: "show-label",
          default: "true",
          description:
            "Ajouter/mettre l'élément «&nbsp;show-label&nbsp;» (affiché l'étiquette) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous ne voulez pas afficher d'étiquette ou à «&nbsp;true&nbsp;» (vrai) pour afficher une étiquette. La valeur par défaut est mise à «&nbsp;true&nbsp;»."
        },
        {
          id: 4,
          name: "label",
          default: "Sélectionnez une industrie / Sélectionnez les industries",
          description:
            "Ajouter/mettre l'élément «&nbsp;label&nbsp;» (étiquette) de la balise du widget avec du texte contenant l'étiquette personnalisé à montrer. Si l'élément «&nbsp;label&nbsp;» est vide ou manquant, une étiquette par défaut sera affichée."
        },
        {
          id: 5,
          name: "placeholder-text",
          default: "Tapez pour rechercher",
          description:
            "Ajouter/mettre l'élément «&nbsp;placeholder-text&nbsp;» (espace de texte réservé) de la balise du widget avec du texte contenant l'espace personnalisé réservé à montrer. Si l'élément «&nbsp;placeholder-text&nbsp;» n'est pas inclus, un espace réservé par défaut sera affiché. Pour cacher le texte par défaut, mettre l'élément «&nbsp;placeholder-text&nbsp;» à une chaîne vide (&quot;&nbsp;&quot;)."
        },
        {
          id: 6,
          name: "show-code",
          default: "true",
          description: `Ajouter/mettre l'élément «&nbsp;show-code&nbsp;» (affiché le code) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous ne voulez pas afficher le numéro de code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> ou à «&nbsp;true&nbsp;» (vrai) pour afficher le numéro de code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>. La valeur par défaut est mise à «&nbsp;true&nbsp;».`
        },
        {
          id: 7,
          name: "visibility",
          default: "true",
          description: `Ajouter/mettre l'élément «&nbsp;visibility&nbsp;» (visibilité) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous voulez cacher la boîte d'entrée ou à «&nbsp;true&nbsp;» (vrai) pour la montrer. Si l'élément est mis sur «&nbsp;false&nbsp;», la balise «&nbsp;input-code&nbsp;» devra se voir attribuer un code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> par défaut (industrie) afin que le widget fonctionne correctement. La valeur par défaut est mise à «&nbsp;true&nbsp;».`
        },
        {
          id: 8,
          name: "input-code",
          default: "",
          description: `Ajouter/mettre l'élément «&nbsp;input-code&nbsp;» (saisie de code) de la balise du widget avec du texte contenant le code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> à utiliser par défaut. Si l'élément «&nbsp;input-code&nbsp;» est vide ou manquant, aucun titre par défaut va être affiché et la recherche normale est affichée.`
        }
        /* {
          id: 9,
          name: "@change",
          default: "",
          description:
            "Si Vue.js est utilisé, l'élément «&nbsp;@change&nbsp;» contient le nom de la fonction à utiliser pour récupérer les données en format objet."
        } */
      ]
    },
    {
      id: "3",
      name: "Wage",
      nameFr: "Salaire",
      tag: "open-services-wage-widget",
      return: "wage",
      widget_tag: `open-services-wage-widget`,
      widget_source_en: `<a href="https://open.canada.ca/data/en/dataset/adad580f-76b0-4502-bd05-20c125de9116" target="_blank">Wages Dataset, Employment and Social Development Canada (ESDC), 2019</a><br /><br />Some combinations of industry and location may not have data due to data limitations. No calculations have been made.`,
      widget_source_fr: `<a href="https://ouvert.canada.ca/data/fr/dataset/adad580f-76b0-4502-bd05-20c125de9116" target="_blank">Jeu de données sur les salaires, Emploi et Développement social Canada (EDSC), 2019</a><br /><br />Certaines combinaisons d'industrie et d'emplacement peuvent ne pas avoir de données en raison de limites des données. Aucun calcul n'a été effectué.`,
      collapses: [
        {
          title: `Why are certain industries named and grouped the way they are?`,
          title_fr: `Pourquoi certaines industries sont nommées et groupées de cette façon?`,
          text: `<p>
            The information presented here is based on the most recent (2017) Canadian version of the 
            <a href="http://www.statcan.gc.ca/eng/subjects/standard/naics/2017v2/introduction" target="_blank">North American Industry Classification System</a>. We use the 
            standard titles, definitions, and examples provided in the <a href="http://www.statcan.gc.ca/eng/subjects/standard/naics/2017v2/introduction" target="_blank"><abbr title="North American Industry Classification System">NAICS</abbr> handbook</a>.
            </p>`,
          text_fr: `<p>
            L'information présenté ici est basé sur la version canadienne la plus récente (2017) du 
            <a href="https://www.statcan.gc.ca/fra/sujets/norme/scian/2017v2/introduction" target="_blank">Système de classification des industries de l'Amérique du Nord</a>. 
            Nous utilisons les titres, les définitions et les exemples standards fourni dans le 
            <a href="https://www.statcan.gc.ca/fra/sujets/norme/scian/2017v2/introduction" target="_blank">guide du <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr></a>.
            </p>`
        },
        {
          title: `Why can't I find information for my industry and/or location?`,
          title_fr: `Pourquoi je ne peux trouver l'information pour mon industrie et/ou l'emplacement?`,
          text: `<p>
            The information may not exist in the combination you've chosen. For example, poppy flowers are not endemic to Canada so poppy farming is not likely to yield 
            results in any province selected.
            </p>
            <p>
            Sometimes an industry is represented by such a small number of businesses in a certain location that we can't share the data without violating the privacy of 
            those businesses. Other times, the information may be inaccurate or missing. In these cases, Statistics Canada has flagged the data as unreliable and we have 
            removed it from our collection.
            </p>`,
          text_fr: `<p>
            L'information peut ne pas existé dans la combinaison que vous avez choisi. Par exemple, le coquelicot n'est pas une espèce endémique du Canada donc l'agriculture 
            du coquelicot ne produirait probablement pas des résultats dans l'une ou l'autre des provinces sélectionnées.
            </p>
            <p>
            Parfois une industrie est représenté par un faible nombre d'entreprises dans un certain emplacement que nous ne pouvons pas partager les données sans 
            faire abstraction à la protection de la vie privée de ses entrprises. En d'autre temps, l'information peut être inexacte ou manquant. Dans ces cas, Statistique 
            Canada a signalé les données comme un fonctionnement incertain et nous les avons retiré de notre collection.
            </p>`
        },
        {
          title: `Why am I being shown a different industry and/or location than the one I chose?`,
          title_fr: `Pourquoi je suis montré une industrie et/ou emplacement différent de ce que j'ai choisi?`,
          text: `<p>
            We always show the most detailed and specific information available. However, if information is not available for your choices, the program will show the most 
            similar industry or location for which information is available.
            </p>
            <p>
            In the case of location, this often means showing you national instead of provincial information.
            </p>
            <p>
            In the case of industry, this often means showing you information from a broader sector instead of the specific industry you chose. For example, we may not 
            have information on resorts in particular but we have information on the traveller accommodation sector (which includes resorts, hotels, motels, and motor 
            homes).
            </p>`,
          text_fr: `<p>
            Nous montrons toujours l'information la plus détaillé et spécifique disponible. Cependant, si l'information n'est pas disponible pour vos choix, le programme va 
            montrer l'industrie ou l'emplacement le plus similaire dont l'information est disponible.
            </p>
            <p>
            Dans le cas de l'emplacement, ceci veut souvent dire qu'on vous montre l'information national au lieu de provincial.
            </p>
            <p>
            Dans le cas de l'industrie, ceci veut souvent dire qu'on vous montre l'information à partir d'un secteur plus générale au lieu d'une industrie 
            spécifique que vous avez choisi. Par exemple, nous n'avons peut-être pas l'information sur les centres de villégiature en particulier mais nous avons 
            l'information sur le secteur de l'hébergement des voyageurs (qui inclus centres de villégiature, hôtels, motels et maisons motorisées).
            </p>`
        },
        {
          title: `What is the wage data for?`,
          title_fr: `Quelle est l'utilité des données sur les salaires?`,
          text: `<p>
            The wage data widget is intended to provide insight into the hourly wage for a given occupation within economic regions of Canada. The dataset we use is 
            prepared by the Labour Market Data, Methods and Analysis Division of Employment and Social Development Canada (ESDC) and it is used in the trends and analysis 
            section of the Government of Canada's <a href="https://www.jobbank.gc.ca/trend-analysis" target="_blank">Job Bank website</a>.
            </p>
            <p>
            The median is used for hourly wage for an occupation. The median wage is preferred over the average wage because it is less sensitive to extreme values and 
            more representative of the typical worker's salary.
            </p>
            <p>
            Occupations are classified using the National Occupation Classification system at the four-digit level.
            </p>
            <p>
            Location data is presented by the Economic Region, a variant of the Standard Geographical Region statistical classification system created by Statistics Canada 
            as a standard geographic unit for the analysis of economic activity within 76 regions across Canada. In our widget, each location is linked to its respective 
            economic region.
            </p>`,
          text_fr: `<p>
            Le widget de données sur les salaires vise à donner un aperçu du salaire horaire pour une profession donnée dans les régions économiques du Canada.
            </p>
            <p>
            Le jeu de données que nous utilisons est préparé par la Division des données, Méthodes et analyse du marché du travail d'Emploi et Développement social Canada (EDSC) 
            où il est utilisé dans la section d'analyse des tendances du <a href="https://www.guichetemplois.gc.ca/analyse-tendances" target="_blank">site Web du Guichet 
            emplois</a> du gouvernement du Canada.
            </p>
            <p>
            La médiane est utilisée pour le salaire horaire d'une profession. Le salaire médian est préféré au salaire moyen car il est moins sensible aux valeurs extrêmes et 
            plus représentatif du salaire type du travailleur.
            </p>
            <p>
            Les professions sont classées à l'aide du système de Classification nationale des professions au niveau des codes à quatre chiffres.
            </p>
            <p>
            Les données d'emplacement sont présentées par région économique, une variante du système de Classification géographique type, normalisées créé par Statistique 
            Canada en tant qu'unité géographique standard pour l'analyse de l'activité économique dans 76 régions du Canada. Dans notre widget, chaque emplacement est lié 
            à sa région économique.
            </p>`
        },
        {
          title: `Which sources of data are used?`,
          title_fr: `Quelles sources de données sont utilisées?`,
          text: `<p>
            Wages are determined using a joint <abbr title="Employment and Social Development Canada">ESDC</abbr> and Statistics Canada methodology. 
            <abbr title="Employment and Social Development Canada">ESDC</abbr> consults other stakeholders during the validation process, such as provincial and territorial 
            governments, other federal departments and external organizations.
            </p>
            <p>
            As the primary source of the dataset, <abbr title="Employment and Social Development Canada">ESDC</abbr> uses Statistics Canada's Labour Force Survey:
            </p>
            <ul>
            <li>the most inclusive, timely and unbiased source of wage data by occupational group</li>
            <li>this survey is conducted using a sample of respondents</li>
            <li>data may be suppressed for reasons of confidentiality or data quality</li>
            </ul>
            <p>
            When the Labour Force Survey data are not available, other sources are considered, including:
            </p>
            <ul>
            <li><abbr title="Employment and Social Development Canada">ESDC</abbr> program data such as Employment Insurance program survey data</li>
            <li>the Census</li>
            <li>provincial and territorial surveys and administrative data</li>
            <li>other organizations administrative data</li>
            </ul>
            <p>
            For the complete process followed by <abbr title="Employment and Social Development Canada">ESDC</abbr>, please see their 
            <a href="https://www.jobbank.gc.ca/trend-analysis/search-wages/wage-methodology" target="_blank">Wage Methodology</a>.
            </p>`,
          text_fr: `<p>
            Les salaires sont déterminés à l'aide d'une méthodologie conjointe d'<abbr title="Emploi et Développement social Canada">EDSC</abbr> et de Statistique Canada. 
            <abbr title="Emploi et Développement social Canada">EDSC</abbr> consulte d'autres intervenants au cours du processus de validation, comme les gouvernements 
            provinciaux et territoriaux, d'autres ministères fédéraux et des organismes externes.
            </p>
            <p>
            En tant que source principale du jeu de données, <abbr title="Emploi et Développement social Canada">EDSC</abbr> utilise l'Enquête sur la population active 
            de Statistique Canada :
            </p>
            <ul>
            <li>la source de données sur les salaires par groupe professionnel la plus inclusive, opportune et impartiale;</li>
            <li>cette enquête est menée auprès d'un échantillon de répondants;</li>
            <li>les données peuvent être supprimées pour des raisons de confidentialité ou de qualité des données.</li>
            </ul>
            <p>
            Lorsque les données de l'Enquête sur la population active ne sont pas disponibles, d'autres sources sont envisagées, notamment :
            </p>
            <ul>
            <li>les données du programme d'<abbr title="Emploi et Développement social Canada">EDSC</abbr>, comme les données d'enquête sur le programme d'assurance-emploi;</li>
            <li>le recensement;</li>
            <li>les enquêtes provinciales et territoriales et les données administratives;</li>
            <li>autres données administratives des organisations.</li>
            </ul>
            <p>
            Pour le processus complet suivi par <abbr title="Emploi et Développement social Canada">EDSC</abbr>, veuillez consulter leur 
            <a href="https://www.guichetemplois.gc.ca/analyse-tendances/recherche-salaires/methodologie-salaires" target="_blank">méthodologie des salaires</a>.
            </p>`
        },
        {
          title: `Have you manipulated the original dataset?`,
          title_fr: `Avez-vous manipulé le jeu de données d'origine?`,
          text: `<p>
            We have removed from the dataset salaries that were presented as an annual salary versus an hourly wage. However, this data can be found from the original source.
            </p>`,
          text_fr: `<p>
            Nous avons supprimé du jeu de données les salaires qui étaient présentés comme un salaire annuel au lieu d'un salaire horaire. Cependant, ces données peuvent 
            être trouvées à partir de la source d'origine.
            </p>`
        },
        {
          title: `Why is there no data available for some combinations of occupation and location?`,
          title_fr: `Pourquoi n'y a-t-il pas de données disponibles pour certaines combinaisons de profession et d'emplacement?`,
          text: ` <p>
            It is not always possible to determine occupational wages for all economic regions due to data limitations (suppression of data due to low quality and privacy 
            reasons). When this is the case, we will display the minimum wage of the province or territory. When comparing locations or industries, these values will appear 
            as not available (n/a) and without the minimum wage.
            </p>`,
          text_fr: `<p>
            Il n'est pas toujours possible de déterminer les salaires des professions pour toutes les régions économiques en raison des limites des données (suppression 
            des données pour des raisons de faible qualité et de confidentialité). Lorsque c'est le cas, nous afficherons le salaire minimum de la province ou du territoire. 
            Lorsque vous comparez des emplacements ou des industries, ces valeurs non disponibles afficheront comme étant sans objet (s.o.) et sans le salaire minimum.
            </p>`
        },
        {
          title: `How are you able to pull occupational data by searching for an industry?`,
          title_fr: `Comment pouvez-vous extraire des données sur les professions en recherchant une industrie?`,
          text: `<p>
            We have done an extensive analysis to develop a mapping of occupations by industries. Through concordance tables, we are able to link wage data by occupation to 
            their related industries.
            </p>`,
          text_fr: `<p>
            Nous avons effectué une analyse approfondie pour élaborer une cartographie des professions par industrie. Grâce à des tableaux de concordance, 
            nous pouvons relier les données sur les salaires par profession à leurs industries connexes.
            </p>`
        },
        {
          title: `Which datasets is used by the widget?`,
          title_fr: `Quel jeu de données est utilisés par le widget?`,
          text: `<p>
            Our widget is using the <abbr title="Employment and Social Development Canada">ESDC</abbr> 2019 Wages Dataset, which can be downloaded from the Government of Canada's <a href="https://open.canada.ca/data/en/dataset/adad580f-76b0-4502-bd05-20c125de9116" target="_blank">Open Data website</a>.
            </p>`,
          text_fr: `<p>
            Notre widget utilise le jeu de données sur les salaires d'<abbr title="Emploi et Développement social Canada">EDSC</abbr> 2019, qui peut être téléchargé à partir 
            du <a href="https://ouvert.canada.ca/data/fr/dataset/adad580f-76b0-4502-bd05-20c125de9116" target="_blank">site Web de données ouvertes</a> du gouvernement du Canada.
            </p>`
        },
        {
          title: `How soon is the widget updated after a new wage data version is released?`,
          title_fr: `Combien de temps le widget est-il mis à jour suite à la publication d'une nouvelle version des données sur les salaires?`,
          text: ` <p>
            We aim to update the data within 3 months of the new release of new dataset. This dataset is produced yearly, and it is published in the late fall.
            </p>`,
          text_fr: `<p>
            Notre objectif est de mettre à jour les données dans les 3 mois suivant la nouvelle version du nouveau jeu de données. Ce jeu de données est produit chaque année 
            et publié à la fin de l'automne.
            </p>`
        },
        {
          title: `Attribution statement`,
          title_fr: `Énoncé d'attribution`,
          text: `<p> 
            Contains information licensed under the <a href="https://open.canada.ca/en/open-government-licence-canada" target="_blank">Open Government Licence - Canada</a>
            </p>`,
          text_fr: `<p> 
            Contient de l'information visée par la <a href="https://ouvert.canada.ca/fr/licence-du-gouvernement-ouvert-canada" target="_blank">Licence du gouvernement ouvert - Canada</a>
            </p>`
        }
      ],
      widget_url:
        "https://open.bizpal-perle.ca/libraries/open-services-wage-widget.umd.min.js",
      widget_urlFr:
        "https://ouverts.perle-bizpal.ca/libraries/open-services-wage-widget.umd.min.js",
      description: `Allows a user to input an industry and location, returning a table of the common occupations associated with the chosen industry and their average hourly wage. The returned data is drawn from the Statistics Canada Labour Force Survey. The user may also choose to compare two locations and/or industries.`,
      descriptionFr: `Aide les utilisateurs à trouver le salaire horaire médian pour les professions dans leur industrie et leur emplacement géographique sélectionnés. Les utilisateurs peuvent également comparer deux emplacements et/ou industries. Les données de retour sont tirées du jeu de données sur les salaires de 2019 d'Emploi et Développement social Canada.`,
      imgSrc: require("../../../public/images/wage_example_en.jpg"),
      imgSrcFr: require("../../../public/images/wage_example_fr.jpg"),
      exampleText: `<p>As the user types "Toronto" and "hot dog" the autocomplete shows the preferred terms that match the user's input.
        </p>
        <br />
        <p>
        As the user selects "Mobile food concession", it returns a table containing the occupations associated with the selected industry and their average hourly wage.
        </p>
        <br />
      `,
      exampleTextLong: `<p>
        If the user clicks the "Compare with another location and/or industry" checkbox, an additional menu will appear, allowing the user to select a second location and/or industry. 
        </p>
        <br />
        <p>
        If the user inputs a second location, the table will be updated to include another column with the average hourly wages associated with the new location. 
        </p>
        <br />
        <p>
        If the user inputs a second industry, a new table will appear below the first, containing the common occupations and associated average hourly wages for the new industry.
        </p>`,
      exampleTextFr: `<p>Lorsque l'utilisateur tape «&nbsp;Toronto&nbsp;» et «&nbsp;hot-dog&nbsp;», l'auto-complétion affiche les termes préférés qui correspondent à l'entrée de l'utilisateur.
        </p>
        <br />
        <p>
        Lorsque l'utilisateur sélectionne «&nbsp;Concession alimentaire mobile&nbsp;», le widget renvoie un tableau contenant le salaire horaire médian des professions associées à l'industrie sélectionné.
        </p>
        <br />
        `,
      exampleTextLongFr: `<p>
        Si l'utilisateur clique sur la case à cocher «&nbsp;Comparez avec un autre emplacement et/ou industrie&nbsp;», un menu supplémentaire apparaît, permettant à l'utilisateur de sélectionner un deuxième emplacement et/ou industrie.
        </p>
        <br />
        <p>
        Si l'utilisateur saisit un deuxième emplacement, le tableau sera mis à jour pour inclure une autre colonne avec le salaire horaire médian associé au nouvel emplacement.
        </p>
        <br />
        <p>
        Si l'utilisateur saisit une deuxième industrie, un nouveau tableau apparaîtra sous la première, contenant les professions courantes et les salaires horaires médian associés pour la nouvelle industrie.
        </p>
        `,
      customAttributes: [
        {
          id: 1,
          name: "lang",
          default: "en",
          description:
            "Add/set the 'lang' element within the widget tag to the language of your choice ('en' is English and 'fr' is French). Default value is set to 'en'."
        },
        {
          id: 2,
          name: "show-inputs",
          default: "true",
          description: `Add/set the 'show-inputs' element within the widget tag to 'false' if you do not want the user to be able to select the intial location and industry, or 'true' to allow the user to select the location and industry. If set to 'false', the 'sgc-code' tag element will have to be assigned a default <abbr title="Standard Geographical Classification">SGC</abbr> (location) code, and the 'naics-code' tag element will have to be assigned a default <abbr title="North American Industry Classification System">NAICS</abbr> (industry) code in order for the widget to function properly. Default value is set to 'true'.`
        },
        {
          id: 3,
          name: "label",
          default: "Median Hourly Full-time Wage",
          description:
            "Add/set the 'label' element within the widget tag with text containing the customized label to show. If 'label' element is empty or missing, a default label will be shown."
        },
        {
          id: 4,
          name: "naics-code",
          default: "",
          description: `Add/set the 'naics-code' element within the widget tag with text containing the <abbr title="North American Industry Classification System">NAICS</abbr> (industry) code to use by default. If the 'naics-code' element is empty or missing, no default industry will be shown and normal search shows.`
        },
        {
          id: 5,
          name: "sgc-code",
          default: "",
          description: `Add/set the 'sgc-code' element within the widget tag with text containing the <abbr title="Standard Geographical Classification">SGC</abbr> (location) code to use by default. If the 'sgc-code' element is empty or missing, no default location will be shown and normal search shows.`
        },
        {
          id: 6,
          name: "show-compare",
          default: "true",
          description:
            "Add/set the 'show-compare' element within the widget tag to 'false' if you do not want the user to be able to select a second location and/or industry, or 'true' to allow the user to select a second location and/or industry. If set to 'false', the 'Compare to another location and/or industry' checkbox will be hidden from the user. Default value is set to 'true'."
        },
        {
          id: 7,
          name: "naics-compare",
          default: "",
          description: `Add/set the 'naics-compare' element within the widget tag with text containing the <abbr title="North American Industry Classification System">NAICS</abbr> (industry) code to use as a default comparison. If the 'naics-compare' element is empty or missing, no default industry comparison will be displayed and the default search bar will appear when the 'Compare with another location and/or industry' checkbox is selected.`
        },
        {
          id: 8,
          name: "sgc-compare",
          default: "",
          description: `Add/set the 'sgc-compare' element within the widget tag with text containing the <abbr title="Standard Geographical Classification">SGC</abbr> (location) code to use as a default comparison. If the 'sgc-compare' element is empty or missing, no default location comparison will be displayed and the default search bar will appear when the 'Compare with another location and/or industry' checkbox is selected.`
        }
      ],
      customAttributesFr: [
        {
          id: 1,
          name: "lang",
          default: "en",
          description:
            "Ajouter/mettre l'élément «&nbsp;lang&nbsp;» de la balise du widget à la langue de votre choix (fr = français et en = anglais). La valeur par défaut est mise à «&nbsp;en&nbsp;»."
        },
        {
          id: 2,
          name: "show-inputs",
          default: "true",
          description: `Ajouter/mettre l'élément «&nbsp;show-inputs&nbsp;» (affiché la saisie) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous souhaitez que l'utilisateur ne puisse pas sélectionner leur emplacement ou leur industrie, ou «&nbsp;true&nbsp;» (vrai) pour lui permettre de les sélectionner. Si l'élément est mis sur «&nbsp;false&nbsp;», la balise «&nbsp;sgc-code&nbsp;» (code <abbr title="Classification géographique type">CGT</abbr>) devra se voir attribuer un code <abbr title="Classification géographique type">CGT</abbr> (emplacement) par défaut et l'élément de balise «&nbsp;naics-code&nbsp;» (code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>) devra se voir attribuer un code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> par défaut (industrie) afin que le widget fonctionne correctement. La valeur par défaut est «&nbsp;true&nbsp;» (vrai).`
        },
        {
          id: 3,
          name: "label",
          default: "Salaire horaire médian à temps plein",
          description:
            "Ajouter/mettre l'élément «&nbsp;label&nbsp;» (étiquette) de la balise du widget avec du texte contenant l'étiquette personnalisé à montrer. Si l'élément «&nbsp;label&nbsp;» est vide ou manquant, une étiquette par défaut sera affichée."
        },
        {
          id: 4,
          name: "naics-code",
          default: "",
          description: `Ajouter/mettre l'élément «&nbsp;naics-code&nbsp;» (code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>) de la balise du widget avec du texte contenant le code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> (industrie) à utiliser par défaut. Si l'élément «&nbsp;naics-code&nbsp;» est vide ou manquant, par défaut aucune industrie ne sera affiché et la recherche normale sera présentée.`
        },
        {
          id: 5,
          name: "sgc-code",
          default: "",
          description: `Ajouter/mettre l'élément «&nbsp;sgc-code&nbsp;» (code <abbr title="Classification géographique type">CGT</abbr>) de la balise du widget avec du texte contenant le code <abbr title="Classification géographique type">CGT</abbr> (emplacement) à utiliser par défaut. Si l'élément «&nbsp;sgc-code&nbsp;» est vide ou manquant, aucun emplacement par défaut ne sera affiché et la recherche normale sera présentée.`
        },
        {
          id: 6,
          name: "show-compare",
          default: "true",
          description:
            "Ajouter/mettre l'élément «&nbsp;show-compare&nbsp;» (affiché la comparaison) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous souhaitez que l'utilisateur ne puisse pas sélectionner un deuxième emplacement et/ou industrie, ou «&nbsp;true&nbsp;» (vrai) pour lui permettre de les sélectionner. Si l'élément est mis à «&nbsp;false&nbsp;», la case «&nbsp;Comparez avec un autre emplacement et/ou industrie&nbsp;» va être caché à l'utilisateur. La valeur par défaut est mise à «&nbsp;true&nbsp;»."
        },
        {
          id: 7,
          name: "naics-compare",
          default: "",
          description: `Ajouter/mettre l'élément «&nbsp;naics-compare&nbsp;» (comparé avec <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>) de la balise du widget avec du texte contenant le code SCIAN (industrie) à utiliser comme comparaison par défaut. Si l'élément «&nbsp;naics-compare&nbsp;» est vide ou manquant, aucune industrie de comparaison par défaut ne sera affiché et la recherche normale sera présentée quand la case «&nbsp;Comparez avec un autre emplacement et/ou industrie&nbsp;» est sélectionné.`
        },
        {
          id: 8,
          name: "sgc-compare",
          default: "",
          description: `Ajouter/mettre l'élément «&nbsp;sgc-compare&nbsp;» (comparé avec <abbr title="Classification géographique type">CGT</abbr>) de la balise du widget avec du texte contenant le code <abbr title="Classification géographique type">CGT</abbr> (emplacement) à utiliser comme comparaison par défaut. Si l'élément «&nbsp;sgc-compare&nbsp;» est vide ou manquant, aucun emplacement de comparaison par défaut ne sera affiché et la recherche normale sera présentée quand la case «&nbsp;Comparez avec un autre emplacement et/ou industrie&nbsp;» est sélectionné.`
        }
      ]
    },
    {
      id: "4",
      name: "Industry - Expanded  (Naics 2017 version)",
      nameFr: "Industrie - Étendu (version SCIAN 2017)",
      return: "naics",
      tag: "open-services-tree-naics-lookup",
      widget_tag: `open-services-tree-naics-lookup`,
      widget_source_en: `<a href="https://www23.statcan.gc.ca/imdb/p3VD.pl?Function=getVD&TVD=1181553" target="_blank">North American Industry Classification System (NAICS) Canada 2017, Statistics Canada</a>`,
      widget_source_fr: `<a href="https://www23.statcan.gc.ca/imdb/p3VD_f.pl?Function=getVD&TVD=1181553" target="_blank">Système de classification des industries de l'Amérique du Nord (SCIAN) Canada 2017, Statistique Canada</a>`,
      collapses: [
        {
          title: "About the widget",
          title_fr: "À propos du widget",
          text:
            "<p>This search tool is built to help you identify your industry. The industry list comes from the North American Industry Classification System (NAICS) 2017, version 3.</p>",
          text_fr:
            "<p>Cet outil de recherche est conçu pour vous aider à identifier votre industrie. La liste des industries provient du Système de classification des industries de l'Amérique du Nord (SCIAN) 2017, version 3.</p>"
        },
        {
          title:
            "What is&nbsp;<abbr title='North American Industry Classification System'>NAICS</abbr>?",
          title_fr: `Qu'est-ce que le&nbsp;<abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>?`,
          text: `<p><abbr title='North American Industry Classification System'>NAICS</abbr> is a industry classification system developed by the statistical agencies of Canada, 
          Mexico and the United States. It is designed to provide common definitions of the industrial structure of the three countries and a common statistical framework to facilitate 
          economic analysis.</p>
          <p>The structure of <abbr title='North American Industry Classification System'>NAICS</abbr> is hierarchical. It is composed of five levels.</p>
          <ul>
            <li>level 1: industry sectors (two-digit codes)</li>
            <li>level 2: industry subsectors (three-digit codes)</li>
            <li>level 3: industry groups (four-digit codes)</li>
            <li>level 4: industries (five-digit codes)</li>
            <li>level 5: Canadian industries (six-digit codes)</li>
          </ul>
          <p>Learn more about <a href='https://www.statcan.gc.ca/en/subjects/standard/naics/2017/v3/introduction' target='_blank'><abbr title='North American Industry Classification System'>NAICS</abbr></a>.</p>`,
          text_fr: `<p>Le <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> est un système de classification des industries élaboré par les 
          organismes statistiques du Canada, du Mexique et des États-Unis. Il est conçu pour fournir des définitions communes de la structure industrielle des trois pays et un 
          cadre statistique commun pour faciliter l'analyse économique.</p>
          <p>La structure du <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> est hiérarchique. Il est composé de cinq niveaux.</p>
          <ul>
            <li>niveau 1 : secteurs industriels (codes à deux chiffres)</li>
            <li>niveau 2 : sous-secteurs industriels (codes à trois chiffres)</li>
            <li>niveau 3 : groupes industriels (codes à quatre chiffres)</li>
            <li>niveau 4 : industries (codes à cinq chiffres)</li>
            <li>niveau 5 : industries canadiennes (codes à six chiffres)</li>
          </ul>
          <p>Pour en savoir plus sur le <a href='https://www.statcan.gc.ca/fr/sujets/norme/scian/2017/v3/introduction' target='_blank'><abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr></a>.</p>`
        },
        {
          title: "How does the search work?",
          title_fr: "Comment fonctionne la recherche?",
          text: `<p>As you search industry terms, the search will provide potential matches within the hierarchy of <abbr title='North American Industry Classification System'>NAICS</abbr>. 
          Certain terms may match to multiple industry sectors, but the sector with help you pinpoint under which context your industry falls. For example, if you search "wheat", 
          the search will show you selections under agriculture, manufacturing and wholesale trades. You can choose the one that applies to you.</p>
          <p>The search result may bump up an industry suggestion from its usual position within the <abbr title='North American Industry Classification System'>NAICS</abbr> hierarchy 
          when it is a closer match to your search term(s).</p>`,
          text_fr: `<p>Au fur et à mesure que vous recherchez des termes d'une industrie, la recherche fournira des correspondances potentielles dans la hiérarchie du 
          <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>. Certains termes peuvent correspondre à plusieurs secteurs industriels, 
          mais le secteur vous aide à déterminer dans quel contexte se situe votre industrie. Par exemple, si vous recherchez «&nbsp;blé&nbsp;», la recherche vous montrera des sélections 
          sous les domaines de l'agriculture, la fabrication et le commerce de gros. Vous pouvez choisir celui qui vous concerne.</p>
          <p>Le résultat de la recherche peut faire sortir une suggestion d'industrie de sa position habituelle dans la hiérarchie du 
          <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> lorsqu'elle correspond davantage à vos termes de recherche.</p>`
        },
        {
          title: "Selecting an industry and seeing more details",
          title_fr: "Sélectionner une industrie et voir plus de détails",
          text: `<p>In your search result, you may select the label of an industry to show the definition, a list of possible exclusions and if there are any, the options of 
          seeing more industry examples that fall within this category.</p>
          <p>Once you have made your choice, make your selection by checking the corresponding checkbox and click <b>Save</b> (note: no need to save for Mobile view).</p>
          <p>By default, the list view is expanded. Using the arrows, you are able to collapse industry groups to hide information, or expand to see them again.</p>`,
          text_fr: `<p>Dans votre résultat de recherche, utilisez la case à cocher pour sélectionner l'industrie qui s'applique à vous. Un panneau contenant des 
          informations supplémentaires apparaîtra, vous indiquant la définition, une liste des exclusions possibles et, le cas échéant, les options permettant de 
          voir davantage d'exemples d'industries entrant dans cette catégorie.</p>
          <p>Une fois que vous avez fait votre choix, faites votre sélection et cliquez sur <b>enregistrer</b>.</p>
          <p>Par défaut, la vue de liste est développée. À l'aide des flèches, vous pouvez réduire les groupes industriels pour masquer les informations ou les développer 
          pour les revoir.</p>`
        },
        {
          title: "Display options and selection formats",
          title_fr: "Options d'affichage et formats de sélection",
          text: `<p>The service provider has the options of customizing this search tool. Depending on how they have chosen to use it, you may have the ability to:</p>
          <ul>
            <li>have the tool in English or in French</li>
            <li>select a single industry only or multiple industries that apply</li>
            <li>select industries only from levels 2, 3, 4 or 5 within the <abbr title="North American Industry Classification System">NAICS</abbr> hierarchy</li>
          </ul>
          <p>The data results may be presented to you:</p>
          <ul>
            <li>No results showing</li>
            <li>Showing the industry code and title, the title only or the code only.</li>
          </ul>`,
          text_fr: `<p>Le fournisseur de services a la possibilité de personnaliser cet outil de recherche. Selon la façon dont ils ont choisi de l'utiliser, 
          vous pouvez être en mesure de :</p>
          <ul>
            <li>avoir l'outil en anglais ou en français</li>
            <li>sélectionnez une seule industrie ou en choisir plusieurs qui s'appliquent</li>
            <li>sélectionnez uniquement les industries des niveaux 2, 3, 4 ou 5 de la hiérarchie du <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr></li>
          </ul>
          <p>Les données peuvent vous être présentées :</p>
          <ul>
            <li>Sans résultats d'afficher</li>
            <li>En affichant le code d'industrie et son titre, ou uniquement avec son titre ou encore son code.</li>
          </ul>`
        },
        {
          title: "Seeing too many results?",
          title_fr: "Vous voyez trop de résultats?",
          text: `<p>If you are seeing too many results, you may be using terms that are too broad. Try refining or adding precision to your search. An example of a broad term 
          is "manufacturing". Refine your search by adding what you are manufacturing. (for example: manufacturing → tortilla manufacturing)</p>
          <p>Need to search a short term that is at the root of larger terms? To search “wind” but not winding or window, place an asterisk (*) at the end of your word: wind*</p>`,
          text_fr: `<p>Si vous voyez trop de résultats, vous utilisez peut-être des termes trop généraux. Essayez d'affiner ou de préciser votre recherche. 
          Un exemple de terme large est «&nbsp;fabrication&nbsp;». Affinez votre recherche en ajoutant ce que vous fabriquez (par exemple : fabrication → fabrication de tortillas).</p>
          <p>Vous devez rechercher un terme court qui est à la base de termes plus larges? Pour rechercher «&nbsp;vent&nbsp;» et non pas ventes ou ventilation, 
          placez un astérisque (*) à la fin de votre mot&nbsp;: vent*</p>`
        },
        {
          title: "Not finding any matches?",
          title_fr: "Vous ne trouvez pas de correspondance?",
          text: `<p>Despite containing thousands of terms, you may be using a term that is not yet identified in the list of searchable terms. Try using a synonym or an 
          alternate expression that would bean the same thing. As examples: shoes → Footwear; filming → motion pictures.</p>
          <p>Is the term too broad? For example, the term "logistics" could apply to all industries under the trucking and warehousing industry sector. Try terms that are 
          more precise. Are you involved in air, rail, water or truck transportation or are you involved in storing or warehousing aspect of the logistics industry?</p>
          <p>Typos or alternate spellings could also cause no results. The search is designed to allows a small degree of misspelling, but could fail if the spelling of 
          a terms falls outside of that limit.</p>
          <p>Using terms that fall outside of the context of the industry can impact the search. For example, rather than search the occupation "pet sitter", try "pet sitting".</p>`,
          text_fr: `<p>Bien qu'il contienne des milliers de termes, vous utilisez peut-être un terme qui n'est pas encore identifié dans la liste des termes interrogeables. 
          Essayez d'utiliser un synonyme ou une autre expression qui serait la même chose. À titre d'exemple, au lieu de «&nbsp;fabrication de gougounes&nbsp;», 
          essayer «&nbsp;fabrication de sandales de plage&nbsp;».</p>
          <p>Le terme est-il trop large? Par exemple, le terme «&nbsp;logistique&nbsp;» pourrait s'appliquer à toutes les industries du secteur de l'industrie du 
          camionnage et de l'entreposage. Essayez des termes plus précis. Êtes-vous impliqué dans le transport aérien, ferroviaire, maritime ou par camion ou êtes-vous 
          impliqué dans l'aspect stockage ou entreposage de l'industrie de la logistique?</p>
          <p>Les fautes de frappe ou les orthographes alternatives peuvent également ne donner aucun résultat. La recherche est conçue pour autoriser un petit degré de 
          fautes d'orthographe, mais peut échouer si l'orthographe d'un terme dépasse cette limite.</p>
          <p>L'utilisation de termes dans un contexte différent de l'industrie peut avoir un impact sur la recherche. Par exemple, plutôt que de rechercher le métier 
          «&nbsp;gardien d'animaux domestiques&nbsp;», essayez «&nbsp;service de garderie d'animaux domestiques&nbsp;».</p>`
        },
        {
          title: "What are we doing to improve the search?",
          title_fr: "Que faisons-nous pour améliorer la recherche?",
          text: `<p>We analyze terms that do not have any matches and over time, we attempt to map these to their respective industries where it is possible.</p>`,
          text_fr: `<p>Nous analysons les termes qui n'ont pas de correspondance et au fil du temps, nous essayons dans la mesure du possible de les appliquer à leurs 
          industries respectives.</p>`
        },
        {
          title: "How can I provide feedback?",
          title_fr: "Comment puis-je fournir des commentaires?",
          text:
            "<p>Send us your feedback at <a href='mailto:comments@bizpal.ca?subject=NAICS feedback'>comments@bizpal.ca</a></p>",
          text_fr:
            "<p>Envoyez-nous vos commentaires à <a href='mailto:comments@bizpal.ca?subject=Rétroaction SCIAN'>comments@bizpal.ca</a></p>"
        }
      ],
      widget_url: `${librariesUrl}/open-services-tree-naics-lookup.umd.min.js`,
      widget_urlFr: `${librariesUrl}/open-services-tree-naics-lookup.umd.min.js`,
      description: `Allows a user to input an industry and returns the
      associated code from the North American Industry Classification
      System (NAICS).`,
      descriptionFr: `Permet à un utilisateur d'entrée une industrie 
      et d'obtenir en retour le code associé du Système de classification 
      des industries de l'Amérique du Nord (SCIAN).`,
      imgSrc: require("../../../public/images/naics-tree_example_en.jpg"),
      imgSrcFr: require("../../../public/images/naics-tree_example_fr.jpg"),
      exampleText: `<p>
      As you search industry terms, the search will provide potential matches within the hierarchy of <abbr title="North American Industry Classification System">NAICS</abbr>.
      </p>
     <br />
     <p>
     For example, if you search "Restaurant", the search will show you selections under Accommodation and Food Services, Manufacturing and Other Services. You can choose the ones that applies to you and it will return the selection in an object format with the associated <abbr title="North American Industry Classification System">NAICS</abbr> code.
     </p>
    `,
      exampleTextFr: `<p>Au fur et à mesure que vous recherchez des termes d'une industrie, la recherche fournira des correspondances potentielles dans la hiérarchie du <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>.</p>
      <br />
      <p>
      Par exemple, si vous recherchez «&nbsp;restaurant&nbsp;», la recherche vous montrera des sélections sous les domaine d'Hébergement et services de restauration, Fabrication et Autres services. Vous pouvez choisir celui qui vous concerne et retourne la sélection en format objet avec le code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> associé.
      </p>
    `,
      customAttributes: [
        {
          id: 1,
          name: "multiple",
          default: "false",
          description:
            "Add/set the 'multiple' element within the widget tag to 'false' if you want a single selection, or to 'true' for multiple selections. Default value is set to 'false'."
        },
        {
          id: 2,
          name: "lang",
          default: "en",
          description:
            "Add/set the 'lang' element within the widget tag to the language of your choice ('en' is English and 'fr' is French). Default value is set to 'en'."
        },
        /* {
          id: 3,
          name: "show-label",
          default: "true",
          description:
            "Add/set the 'show-label' element within the widget tag to 'false' if you want no label to show, or 'true' to show a label. Default value is set to 'true'."
        }, */
        {
          id: 4,
          name: "label",
          default: "Select your industry / Select your industries",
          description:
            "Add/set the 'label' element within the widget tag with text containing the customized label for the button (not available on mobile device). If 'label' element is empty or missing, a default label will be shown."
        },
        /*   {
          id: 5,
          name: "placeholder-text",
          default: "Type to search",
          description:
            "Add/set the 'placeholder-text' element within the widget tag with text containing the customized placeholder to show. If the 'placeholder-text' element is not included, a default placeholder will be shown. To hide the default text, set the 'placeholder-text' element to an empty string (&quot;&nbsp;&quot;)."
        }, */
        {
          id: 6,
          name: "show-code",
          default: "true",
          description: `Add/set the 'show-code' element within the widget tag to 'false' if you do not want the <abbr title="North American Industry Classification System">NAICS</abbr> code number to be displayed, or set to 'true' to show the <abbr title="North American Industry Classification System">NAICS</abbr> code number. Default value is set to 'true'.`
        },
        /*   {
          id: 7,
          name: "visibility",
          default: "true",
          description:
            "Add/set the 'visibility' element within the widget tag to 'false' if you want to hide the input box, or 'true' to show it. If set to 'false', the 'input-code' tag element will have to be assigned a default NAICS (industry) code in order for the widget to function properly. Default value is set to 'true'."
        },
        {
          id: 8,
          name: "input-code",
          default: "",
          description:
            "Add/set the 'input-code' element within the widget tag with text containing the NAICS code to use by default. If 'input-code' element is empty or missing, no default title will be shown and normal search shows."
        } */
        {
          id: 9,
          name: "@change",
          default: "",
          description:
            "If using Vue.js, the element '@change' contains the name for the function to use to retrieve the data in an object format."
        },
        {
          id: 10,
          name: "level-selectable",
          default: "2",
          description:
            "Add/set the 'level-selectable' element within the widget tag with the allowed selection digit level. Levels are 2 for 2,3,4 and 6 digits; 3 for 3,4 and 6 digits; 4 for 4 and 6 digits; and 5 for 6 digits. Default value is set to '2'."
        },
        {
          id: 11,
          name: "show-results",
          default: "",
          description:
            "Add/set the 'show-results' element within the widget tag with how you want to show results (not available on mobile device). Options are “”= no results with number of selections in Edit button; “1”= code - label; “2”= label and “3”= code. Default value is set to “”."
        }
      ],
      customAttributesFr: [
        {
          id: 1,
          name: "multiple",
          default: "false",
          description:
            "Ajouter/mettre l'élément «&nbsp;multiple&nbsp;» de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous voulez une seule sélection ou à «&nbsp;true&nbsp;» (vrai) pour une sélection multiple. La valeur par défaut est mise à «&nbsp;false&nbsp;»."
        },
        {
          id: 2,
          name: "lang",
          default: "en",
          description:
            "Ajouter/mettre l'élément «&nbsp;lang&nbsp;» de la balise du widget à la langue de votre choix (fr = français et en = anglais). La valeur par défaut est mise à «&nbsp;en&nbsp;»."
        },
        /*   {
          id: 3,
          name: "show-label",
          default: "true",
          description:
            "Ajouter/mettre l'élément «&nbsp;show-label&nbsp;» (affiché l'étiquette) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous ne voulez pas afficher d'étiquette ou à «&nbsp;true&nbsp;» (vrai) pour afficher une étiquette. La valeur par défaut est mise à «&nbsp;true&nbsp;»."
        }, */
        {
          id: 4,
          name: "label",
          default: "Choisir votre industrie / Choisir vos industries",
          description:
            "Ajouter/mettre l'élément «&nbsp;label&nbsp;» (étiquette) de la balise du widget avec du texte contenant l'étiquette personnalisé du bouton (pas disponible sur appareil mobile). Si l'élément «&nbsp;label&nbsp;» est vide ou manquant, une étiquette par défaut sera affichée."
        },
        /*   {
          id: 5,
          name: "placeholder-text",
          default: "Tapez pour rechercher",
          description:
            "Ajouter/mettre l’élément «&nbsp;placeholder-text&nbsp;» (espace de texte réservé) de la balise du widget avec du texte contenant l’espace personnalisé réservé à montrer. Si l’élément «&nbsp;placeholder-text&nbsp;» n’est pas inclus, un espace réservé par défaut sera affiché. Pour cacher le texte par défaut, mettre l’élément «&nbsp;placeholder-text&nbsp;» à une chaîne vide (&quot;&nbsp;&quot;)."
        }, */
        {
          id: 6,
          name: "show-code",
          default: "true",
          description: `Ajouter/mettre l'élément «&nbsp;show-code&nbsp;» (affiché le code) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous ne voulez pas afficher le numéro de code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr> ou à «&nbsp;true&nbsp;» (vrai) pour afficher le numéro de code <abbr title="Système de classification des industries de l'Amérique du Nord">SCIAN</abbr>. La valeur par défaut est mise à «&nbsp;true&nbsp;».`
        },
        /*   {
          id: 7,
          name: "visibility",
          default: "true",
          description:
            "Ajouter/mettre l'élément «&nbsp;visibility&nbsp;» (visibilité) de la balise du widget à «&nbsp;false&nbsp;» (faux) si vous voulez cacher la boîte d'entrée ou à «&nbsp;true&nbsp;» (vrai) pour la montrer. Si l'élément est mis sur «&nbsp;false&nbsp;», la balise «&nbsp;input-code&nbsp;» devra se voir attribuer un code SCIAN par défaut (secteur d'activité) afin que le widget fonctionne correctement. La valeur par défaut est mise à «&nbsp;true&nbsp;»."
        },
        {
          id: 8,
          name: "input-code",
          default: "",
          description:
            "Ajouter/mettre l'élément «&nbsp;input-code&nbsp;» (saisie de code) de la balise du widget avec du texte contenant le code SCIAN à utiliser par défaut. Si l'élément «&nbsp;input-code&nbsp;» est vide ou manquant, aucun titre par défaut va être affiché et la recherche normale est affichée."
        } */
        {
          id: 9,
          name: "@change",
          default: "",
          description:
            "Si Vue.js est utilisé, l'élément «&nbsp;@change&nbsp;» contient le nom de la fonction à utiliser pour récupérer les données en format objet."
        },
        {
          id: 10,
          name: "level-selectable",
          default: "2",
          description:
            "Ajouter/mettre l'élément «&nbsp;level-selectable&nbsp;» (niveau de sélection) de la balise du widget avec le niveau de chiffre de sélection autorisé. Les niveaux sont 2 pour 2,3,4 et 6 chiffres; 3 pour 3,4 et 6 chiffres; 4 pour 4 et 6 chiffres et; 5 pour 6 chiffres. La valeur par défaut est mise à «&nbsp;2&nbsp;»."
        },
        {
          id: 11,
          name: "show-results",
          default: "",
          description:
            "Ajouter/mettre l'élément «&nbsp;show-results&nbsp;» (afficher résultats) de la balise du widget avec comment vous voulez afficher les resultats (pas disponible sur appareil mobile). Les options sont “”= aucun resultats avec nombre de sélections dans le bouton Éditer; “1”= code - étiquette; “2”= étiquette and “3”= code. La valeur par défaut est mise à une chaîne vide (&quot;&nbsp;&quot;)."
        }
      ]
    },
    {
      id: "5",
      name: "Business Structures Wizard",
      nameFr: "Assistant de structures d'entreprise",
      return: "bet",
      tag: "business-entity-tool",
      widget_tag: `business-entity-tool`,
      widget_source_en: ``,
      widget_source_fr: ``,
      collapses: [],
      widget_url: `${librariesUrl}/business-entity-tool.umd.min.js`,
      widget_urlFr: `${librariesUrl}/business-entity-tool.umd.min.js`,
      description: `Allows a user to answer questions on their business to view which business structure is right for them, and explore the different options available 
        to them before registering their business.`,
      descriptionFr: `Permet à un utilisateur de répondre aux questions sur leur entreprise pour voir quelle structure d'entreprise pourrait leur convenir et explorez les 
        diverses options qui s'offrent à eux avant d'enregistrer leur entreprise.`,
      tool_option: `<p>There are 2 options: we can host the widget for you, or you can host the widget yourself.<br><br>
        <b>Option 1 - Example of the Wizard being hosted by Open Services:</b><br><br>
        <ul><li><a href="/apps/bet-new-brunswick.html" target="_blank">Business Structure Wizard of New Brunswick</a></li>
        <li><a href="/apps/bet-manitoba.html" target="_blank">Business Structure Wizard of Manitoba</a></li></ul><br><br>
        <b>Option 2 - Example of the Wizard being hosted on the requestor server:</b><br><br><a href="https://www.bcregistry.ca/business/auth/home/decide-business" target="_blank">Business Structure Wizard of British Columbia</a>
        <br><br></p>
        <p><a href="mailto:support@bizpal.ca">Contact Us</a> to discuss having a personalized Business Structure Wizard.</p>`,
      tool_optionFr: `<p>Il y a deux options : nous pouvons héberger le widget pour vous ou vous pouvez vous-même héberger le widget.<br><br>
        <b>Option 1 - Exemple de l'assistant héberger par Services ouverts :</b><br><br>
        <ul><li><a href="/apps/bet-nouveau-brunswick.html" target="_blank">Assistant de structures d'entreprise du Nouveau-Brunswick</a></li>
        <li><a href="/apps/bet-manitoba-fr.html" target="_blank">Assistant de structures d'entreprise du Manitoba</a></li><br><br>
        <b>Option 2 - Exemple de l'assistant héberger sur le serveur du demandeur :</b><br><br>
        <a href="https://www2.gov.bc.ca/gov/content/employment-business/business/small-business/choosing-your-business-structure"target="_blank">Assistant de structures 
        d'entreprise de la Colombie-Britannique</a> (en anglais seulement)
        <br><br></p>
        <p>Veuillez <a href="mailto:support@bizpal.ca">nous contacter</a> pour discuter d'obtenir un Assistant de structures d'entreprise personnalisé.</p>`,
      imgSrc: require("../../../public/images/BET_example_en.png"),
      imgSrcFr: require("../../../public/images/BET_example_fr.png"),
      exampleText: `<p>As the user answers the questions on the left side of the wizard, progress bars track the business structure matches on the right side of the wizard.
        </p>`,
      exampleTextFr: `<p>Au moment que l'utilisateur répond aux questions sur le côté gauche de l'assistant, des barres de progression suivent les correspondances de la structure de l'entreprise sur le côté droit de l'assitant.
        </p>`,
      customAttributes: [],
      customAttributesFr: []
    }
  ],
  widget: {}
};

// mutations/actions/getters
export const mutations = {
  SET_WIDGET(state, widget) {
    state.widget = widget;
  }
};

export const actions = {
  fetchWidget({ commit, getters, state }, id) {
    if (id == state.widget.id) {
      return state.widget;
    }
    var widget = getters.getWidgetById(id);
    commit("SET_WIDGET", widget);
    //console.log(widget);
    if (typeof widget === "undefined") {
      (widget = "error"), console.log(widget);
      return widget;
    }
    return widget;
  }
};

export const getters = {
  getWidgetById: state => id => {
    return state.widgets.find(widget => widget.id === id);
  }
};
